import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// ----------- Type Definitions -----------
export interface EmailTemplate {
  id?: string;
  type: string;
  generated_type?: string;
  applicable_types?: string[];
  name: string;
  subject: string;
  html: string;
  created_at?: string;
  updated_at?: string;
  dealer_id?: string;
}

export interface CreateEmailTemplateRequest {
  type: string;
  generated_type?: string;
  applicable_types?: string[];
  name: string;
  subject: string;
  html: string;
  metadata?: {
    tracking_id?: string;
  };
}

export interface UpdateEmailTemplateRequest {
  type?: string;
  generated_type?: string;
  applicable_types?: string[];
  name?: string;
  subject?: string;
  html?: string;
  metadata?: {
    tracking_id?: string;
  };
}

export interface EmailTemplateResponse {
  data: EmailTemplate[];
  total: number;
  page: number;
  limit: number;
}

class EmailTemplateApiService {
  static ENDPOINT = 'api/v2/templates';

  static async getEmailTemplates(dealerId: string, params?: {
    skip?: number;
    limit?: number;
    type?: string;
  }): Promise<APIResponseType<EmailTemplateResponse>> {
    try {
      const queryParams = {
        ...params,
        dealer: dealerId,
      };

      const response = await apiService.get<APIResponseType<EmailTemplateResponse>>(
        this.ENDPOINT,
        { params: queryParams }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getEmailTemplateById(dealerId: string, templateId: string): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.get<APIResponseType<EmailTemplate>>(
        `${this.ENDPOINT}/${templateId}`,
        { params: { dealer: dealerId } }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async createEmailTemplate(dealerId: string, data: CreateEmailTemplateRequest): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.post<APIResponseType<EmailTemplate>>(
        this.ENDPOINT,
        data,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async updateEmailTemplate(dealerId: string, templateId: string, data: UpdateEmailTemplateRequest): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.put<APIResponseType<EmailTemplate>>(
        `${this.ENDPOINT}/${templateId}`,
        data,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteEmailTemplate(dealerId: string, templateId: string): Promise<APIResponseType<any>> {
    try {
      const response = await apiService.delete<APIResponseType<any>>(
        `${this.ENDPOINT}/${templateId}`,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getTemplateTypes(): Promise<APIResponseType<{ types: string[] }>> {
    try {
      const response = await apiService.get<APIResponseType<{ types: string[] }>>(
        `${this.ENDPOINT}/types`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  private static isMutationSuccess(statusCode?: number): boolean {
    if (statusCode == null) return true;
    return statusCode >= 200 && statusCode < 300;
  }

  static async approveEmailTemplate(
    dealerId: string,
    templateId: string,
  ): Promise<APIResponseType<unknown>> {
    const dealerTrim = dealerId?.trim();
    const idTrim = templateId?.trim();
    if (!dealerTrim || !idTrim) {
      throw new Error('Dealer and template ID are required');
    }
    const response = await apiService.post<unknown>(
      `${EmailTemplateApiService.ENDPOINT}/${idTrim}/approve`,
      {},
      { params: { dealer: dealerTrim } },
      false,
    );
    if (!EmailTemplateApiService.isMutationSuccess(response.statusCode)) {
      const msg = response.message ? String(response.message) : '';
      if (msg) throw new Error(msg);
    }
    await apiService.invalidateCacheByPrefix(EmailTemplateApiService.ENDPOINT, 'GET');
    return response;
  }

  static async rejectEmailTemplate(
    dealerId: string,
    templateId: string,
  ): Promise<APIResponseType<unknown>> {
    const dealerTrim = dealerId?.trim();
    const idTrim = templateId?.trim();
    if (!dealerTrim || !idTrim) {
      throw new Error('Dealer and template ID are required');
    }
    const response = await apiService.post<unknown>(
      `${EmailTemplateApiService.ENDPOINT}/${idTrim}/reject`,
      {},
      { params: { dealer: dealerTrim } },
      false,
    );
    if (!EmailTemplateApiService.isMutationSuccess(response.statusCode)) {
      const msg = response.message ? String(response.message) : '';
      if (msg) throw new Error(msg);
    }
    await apiService.invalidateCacheByPrefix(EmailTemplateApiService.ENDPOINT, 'GET');
    return response;
  }

  static async bulkApproveEmailTemplates(
    dealerId: string,
    ids: string[],
  ): Promise<APIResponseType<unknown>> {
    const dealerTrim = dealerId?.trim();
    const normalizedIds = ids.map(id => String(id).trim()).filter(Boolean);
    if (!dealerTrim || !normalizedIds.length) {
      throw new Error('Dealer and template IDs are required');
    }
    const response = await apiService.post<unknown>(
      `${EmailTemplateApiService.ENDPOINT}/approve/bulk`,
      { ids: normalizedIds },
      { params: { dealer: dealerTrim } },
      false,
    );
    if (!EmailTemplateApiService.isMutationSuccess(response.statusCode)) {
      const msg = response.message ? String(response.message) : '';
      if (msg) throw new Error(msg);
    }
    await apiService.invalidateCacheByPrefix(EmailTemplateApiService.ENDPOINT, 'GET');
    return response;
  }

  static async bulkRejectEmailTemplates(
    dealerId: string,
    ids: string[],
  ): Promise<APIResponseType<unknown>> {
    const dealerTrim = dealerId?.trim();
    const normalizedIds = ids.map(id => String(id).trim()).filter(Boolean);
    if (!dealerTrim || !normalizedIds.length) {
      throw new Error('Dealer and template IDs are required');
    }
    const response = await apiService.post<unknown>(
      `${EmailTemplateApiService.ENDPOINT}/reject/bulk`,
      { ids: normalizedIds },
      { params: { dealer: dealerTrim } },
      false,
    );
    if (!EmailTemplateApiService.isMutationSuccess(response.statusCode)) {
      const msg = response.message ? String(response.message) : '';
      if (msg) throw new Error(msg);
    }
    await apiService.invalidateCacheByPrefix(EmailTemplateApiService.ENDPOINT, 'GET');
    return response;
  }

  static async downloadEmailTemplate(dealerId: string, templateId: string): Promise<APIResponseType<Blob>> {
    try {
      const response = await apiService.get<Blob>(
        `${this.ENDPOINT}/download/${templateId}`,
        { 
          params: { dealer: dealerId },
          responseType: 'blob'
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default EmailTemplateApiService;
